import React, { useState } from 'react';
import { ThemeProvider, CssBaseline, Box, BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import { Home as HomeIcon, Favorite as FavoriteIcon, LocationOn as LocationIcon, Person as PersonIcon, Add as AddIcon } from '@mui/icons-material';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import { theme, darkTheme } from './theme';
import HomeScreen from './components/HomeScreen';
import MapView from './components/MapView';
import RestaurantCard from './components/RestaurantCard';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { Restaurant } from './types';

// Pages
import Home from './pages/Home';
import FavoritesPage from './pages/FavoritesPage';
import NearbyPage from './pages/NearbyPage';
import AccountPage from './pages/AccountPage';
import RestaurantDetailPage from './pages/RestaurantDetailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AddFavoriteRestaurant from './pages/AddFavoriteRestaurant';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import InstallPage from './pages/InstallPage';
import PrivateRoute from './components/PrivateRoute';

const MainContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSelector((state: RootState) => state.auth);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);

  const handleRestaurantSelect = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    navigate(`/restaurant/${restaurant.id}`);
  };

  const handleBottomNavChange = (event: React.SyntheticEvent, newValue: number) => {
    if (!token && (newValue === 1 || newValue === 2 || newValue === 3)) {
      navigate('/login');
      return;
    }

    switch (newValue) {
      case 0:
        navigate('/');
        break;
      case 1:
        navigate('/favorites');
        break;
      case 2:
        navigate('/nearby');
        break;
      case 3:
        navigate('/add-restaurant');
        break;
      case 4:
        navigate('/account');
        break;
    }
  };

  // Get the current bottom nav value based on the current route
  const getBottomNavValue = () => {
    const path = location.pathname;
    if (path === '/') return 0;
    if (path === '/favorites') return 1;
    if (path === '/nearby') return 2;
    if (path === '/add-restaurant') return 3;
    if (path === '/account') return 4;
    return 0;
  };

  // Check if the current route should show the bottom navigation
  const shouldShowBottomNav = () => {
    const path = location.pathname;
    return !path.startsWith('/login') && 
           !path.startsWith('/register') && 
           !path.startsWith('/forgot-password') && 
           !path.startsWith('/reset-password') &&
           !path.startsWith('/install');
  };

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Routes>
          <Route path="/" element={<PrivateRoute><Home /></PrivateRoute>} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/nearby" element={<NearbyPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/restaurant/:id" element={<RestaurantDetailPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/add-restaurant" element={<AddFavoriteRestaurant />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/install" element={<InstallPage />} />
        </Routes>
        {shouldShowBottomNav() && (
          <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
            <BottomNavigation
              value={getBottomNavValue()}
              onChange={handleBottomNavChange}
              showLabels
            >
              <BottomNavigationAction label="Home" icon={<HomeIcon />} />
              <BottomNavigationAction label="Favorites" icon={<FavoriteIcon />} />
              <BottomNavigationAction label="Nearby" icon={<LocationIcon />} />
              <BottomNavigationAction label="Add" icon={<AddIcon />} />
              <BottomNavigationAction label="Account" icon={<PersonIcon />} />
            </BottomNavigation>
          </Paper>
        )}
        <PWAInstallPrompt />
      </Box>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <MainContent />
    </Router>
  );
};

export default App; 